import { Controller, Control, FieldValues, Path } from "react-hook-form";
import { DatePicker, DatePickerInput } from "@carbon/react";

export type DateFieldProps<TFieldValues extends FieldValues> = {
  /** The RHF field name — also becomes the input's `id`, same convention as
   * `TextField`/`SelectField` (what makes `getByLabelText` resolve). */
  name: Path<TFieldValues>;
  /** The field's visible label (already translated by the caller). */
  label: string;
  control: Control<TFieldValues>;
  placeholder?: string;
  /** Optional Carbon helper text shown under the input. */
  helperText?: string;
  /**
   * flatpickr date-format tokens (https://flatpickr.js.org/formatting/), controlling BOTH what
   * the calendar displays AND the literal string handed to `field.onChange` (flatpickr's own
   * `onChange` hook reports `dateStr` pre-formatted to this string, not a `Date` object).
   *
   * Defaults to `"Y-m-d"` — plain ISO `yyyy-MM-dd` — because that is the exact wire shape the
   * pre-Carbon `<input type="date">` this field replaces always produced, and what
   * `CompanyProfileUpdateRequest.CertificationDetailRequest#expirationDate` (a Java
   * `LocalDate`, no `@JsonFormat` override) expects Jackson to deserialize. Only override this
   * for a field with a genuinely different on-the-wire contract — changing it changes what gets
   * sent to the backend, not just what's displayed.
   */
  dateFormat?: string;
  minDate?: string | Date;
  maxDate?: string | Date;
};

/**
 * A Carbon `DatePicker`/`DatePickerInput` pair bound to a React Hook Form field via `Controller`
 * — the `DatePicker` counterpart to `TextField`/`SelectField`. First use of Carbon `DatePicker`
 * anywhere in this migration (Certifications' expiration date, Task 9) — added here rather than
 * wired inline because the same contract (RHF `Controller`, `invalid`/`invalidText` straight from
 * `fieldState.error`, label/id pairing) reads exactly like the other two primitives and is
 * already flagged (Task 3's report, Task 8's own notes-for-Task-9) as needed again by any later
 * flow that grows a date field.
 *
 * Unlike `TextField`/`SelectField`, this does NOT spread `{...field}` onto the underlying Carbon
 * component: `DatePicker`'s own `onChange` is a flatpickr hook, `(selectedDates, dateStr,
 * instance) => void`, not a plain DOM change event — `field.onChange` is called explicitly with
 * `dateStr`, the already-formatted string (see `dateFormat` above), never the `Date` object
 * flatpickr also hands back. That's the one behaviour this component exists to get right: RHF's
 * value for this field is always a plain string in the configured wire format, exactly like
 * `TextField`'s value is always the native input's string.
 *
 * `allowInput` is enabled so a user can type the date directly (not just pick it from the
 * calendar) — matching `TextInput`'s own native-typing affordance and making this testable via
 * `userEvent.type` the same way every other field in this codebase already is.
 */
function DateField<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  placeholder,
  helperText,
  dateFormat = "Y-m-d",
  minDate,
  maxDate,
}: DateFieldProps<TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <DatePicker
          datePickerType="single"
          dateFormat={dateFormat}
          allowInput
          value={field.value || undefined}
          minDate={minDate}
          maxDate={maxDate}
          onChange={(_selectedDates: Date[], dateStr: string) =>
            field.onChange(dateStr)
          }
        >
          <DatePickerInput
            id={name}
            labelText={label}
            placeholder={placeholder}
            helperText={helperText}
            invalid={Boolean(fieldState.error)}
            invalidText={fieldState.error?.message}
            // flatpickr (even with `allowInput`) only fires ITS OWN `onChange` hook (the
            // `DatePicker`-level one above) on Enter or a calendar click — NOT on a plain
            // blur/Tab away after typing (verified against this exact component: typing a full
            // date then tabbing away left the literal text sitting in the DOM input while RHF's
            // own value stayed ""). This `onBlur` is the fix, and deliberately reads the RAW DOM
            // value rather than wiring a plain `onChange` per keystroke: `DatePicker`'s own
            // `value` prop is controlled from `field.value` above, so committing on every
            // keystroke would round-trip a PARTIAL string (e.g. "2", "20") back through that
            // controlled `value` before the user finishes typing — flatpickr fails to parse a
            // partial date against `dateFormat` and clears the input outright (confirmed by
            // reproducing it before landing on this fix). Committing once, on blur, with the
            // fully-typed string already in the DOM, avoids that entirely.
            onBlur={(event) => {
              const rawValue = (event.target as HTMLInputElement).value;
              if (rawValue !== field.value) {
                field.onChange(rawValue);
              }
              field.onBlur();
            }}
          />
        </DatePicker>
      )}
    />
  );
}

export default DateField;
